// grading/realization.ts - the coarse realization factor R (ENGINE_DESIGN.md §7.2).
//
// R discounts (or boosts) a hand's raw equity for how much of it hero actually gets to realize by
// showdown, given position and hand type. It is DELIBERATELY coarse and test-pinned (§7.2): OOP
// marginal hands realize less than their equity (~0.85), IP more (~1.05), and draws sit in between by
// type. This is an estimate, labeled as one everywhere it surfaces - never dressed up as solver truth.
import { handClassRank } from '@postflop/schema';
/** Made-hand strength at/above which realization stops being penalized (a strong made hand realizes
 *  ~fully regardless of position - it can bet/call three streets). Mirrors baseline's VALUE_MIN. */
const STRONG_MADE = handClassRank('top_pair_good_kicker');
/** Draw flags that carry real, position-independent implied odds (they can improve to the nuts). */
const STRONG_DRAW_FLAGS = ['nut_flush_draw', 'flush_draw', 'oesd', 'double_gutshot', 'combo_draw'];
const hasAny = (flags, of) => flags.some((f) => of.includes(f));
/**
 * Coarse realization factor for a hero hand at a decision point. Bounded [0.6, 1.1].
 *
 *   strong made hand      → ~1.0  (realizes fully; position barely matters)
 *   marginal made / weak  → IP 1.05, OOP 0.85  (the canonical §7.2 numbers)
 *   strong draw           → IP 1.0,  OOP 0.9   (implied odds offset some OOP penalty)
 *   weak draw / air        → IP 0.9,  OOP 0.7   (rarely gets there OOP)
 */
export const realizationFactor = (handClass, positionClass, drawFlags = []) => {
    const ip = positionClass === 'IP';
    const r = handClassRank(handClass);
    if (r >= STRONG_MADE)
        return ip ? 1.0 : 0.95; // strong made hand realizes ~fully.
    if (handClass === 'strong_draw' || hasAny(drawFlags, STRONG_DRAW_FLAGS)) {
        return ip ? 1.0 : 0.9; // a real draw with implied odds.
    }
    // marginal made hand (weak pair / second pair / top pair weak kicker) - the canonical case.
    if (r >= handClassRank('weak_pair'))
        return ip ? 1.05 : 0.85;
    // weak draw / ace-high / air - realizes poorly, especially OOP.
    return ip ? 0.9 : 0.7;
};
//# sourceMappingURL=realization.js.map